import {
  ChevronDownIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { FilePlus02Icon } from '@langchain/untitled-ui-icons';
import { LinearProgress, Modal, ModalDialog } from '@mui/joy';
import {
  OnChangeFn,
  RowSelectionState,
  createColumnHelper,
} from '@tanstack/react-table';

import { StarsIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import { CloneDatasetExamplesModal } from '@/Pages/Dataset/components/CloneDatasetExamplesModal';
import { Example } from '@/Pages/Example';
import { CodeLanguageType } from '@/components/Code/types';
import { DataGridColumnVisibilityPopover } from '@/components/DataGrid/DataGridColumnVisibilityPopover';
import { DeleteModal } from '@/components/Delete';
import { useEppoFeatureFlag } from '@/context/hooks/useInitializeEppo';
import { Button, IconButton, Input, Text, Tooltip } from '@/design-system';
import { GroupedTabs } from '@/design-system/components/GroupedTabs/GroupedTabs';
import { useDataGridState } from '@/hooks/useDataGridState';
import { useLanguagePreference } from '@/hooks/useLanguagePreferenceContext/useLanguagePreference';
import { usePermissions } from '@/hooks/usePermissions';
import { useSortingState } from '@/hooks/useSortingState';
import {
  useCloneDatasetMutation,
  useCountExamples,
  useDatasetSplits,
  useExamples,
  useOrganizationId,
  useUpdateDatasetSplitsMutation,
} from '@/hooks/useSwr';
import MetadataIcon from '@/icons/MetadataIcon.svg?react';
import {
  DatasetDataType,
  DatasetSchema,
  ExampleSchema,
  GetExamplesQueryParams,
} from '@/types/schema';
import {
  apiDatasetsPath,
  apiExamplesPath,
  appDatasetsPath,
  appExamplePath,
  appOrganizationPath,
} from '@/utils/constants';

import { CommandItem } from '../Command';
import { DataGrid, DataGridPagination } from '../DataGrid';
import {
  emulateNativeClick,
  emulateNativeMiddleClick,
  getTableSelectColumnDef,
} from '../DataGrid.utils';
import { UploadExamplesToExistingDatasetPane } from '../DatasetCrudPane/UploadExamplesToExistingDatasetPane';
import { ExampleCrudPane } from '../ExampleCrudPane/ExampleCrudPane';
import { KVOnlyExampleCrudPane } from '../ExampleCrudPane/KVOnlyExampleCrudPane';
import { MultiDeleteModal } from '../MultiDelete';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import {
  PopoverDropdownMenu,
  PopoverDropdownMenuItem,
} from '../PopoverDropdownMenu';
import { SplitViewPane } from '../SplitViewPane';
import {
  GenerateSyntheticExamplesPopoverContent,
  SyntheticExamplesPane,
} from '../SyntheticExamplePane/SyntheticExamplePane';
import useToast from '../Toast/useToast';
import { COLUMNS_DEF, getActionColumn } from './components/Columns';
import { ExamplesFilterBar } from './components/ExamplesFilterBar';
import { SplitPickerPopoverContent } from './components/SplitPickerPopoverContent';
import { useExamplesTable } from './hooks/useExamplesTable';
import {
  ExampleFilter,
  ExamplesSearchModel,
} from './types/ExamplesSearchModel';
import { countNumQueries } from './utils';

const columnHelper = createColumnHelper<ExampleSchema>();
const pluralRules = new Intl.PluralRules('en');
const COLUMN_VISIBILITY_STATE_KEY = 'ls:ui:examples-table-columns';

interface TableProps {
  rowSelection: RowSelectionState;
  setRowSelection: OnChangeFn<RowSelectionState>;
  filter: GetExamplesQueryParams;
  onMutate?: () => void;
  isReadOnly?: boolean;
  dataType?: DatasetDataType;
  dataset?: DatasetSchema;
  totalExamplesCount?: number;
}

export const ExamplesTable = ({
  rowSelection,
  setRowSelection,
  filter,
  onMutate,
  isReadOnly,
  dataType,
  dataset,
  totalExamplesCount: exampleCount,
}: TableProps) => {
  'use no memo';
  const organizationId = useOrganizationId();

  // Feature flags
  const showExamplesTableToggles = useEppoFeatureFlag({
    featureFlag: 'examplesTableToggles',
    flagType: 'boolean',
    defaultValue: false,
  });
  const navigate = useNavigate();
  const {
    paginationModel,
    setPaginationModel,
    columnVisibilityModel,
    setColumnVisibilityModel,
    resetPaginationModel,
  } = useDataGridState({
    columnVisibilityStateKey: COLUMN_VISIBILITY_STATE_KEY,
  });
  const [sortingState, setSortingState] = useSortingState('recently_created');

  // Display mode state
  const [displayMode, setDisplayMode] = useState<'compact' | 'full'>('compact');
  // Language preference for JSON/YAML toggle
  const [language, setLanguage] = useLanguagePreference(
    'examples-table-',
    'json'
  );

  const { datasetId } = useParams();
  const [filterToSplit, setFilterToSplit] = useState<string | null>(null);
  const [isCreatingSplit, setIsCreatingSplit] = useState(false);
  const [isSplitPickerPopoverOpen, setIsSplitPickerPopoverOpen] =
    useState(false);
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  const [
    isCloneSelectedExamplesModalOpen,
    setIsCloneSelectedExamplesModalOpen,
  ] = useState(false);
  const [newSplitName, setNewSplitName] = useState('');
  const [currentFilters, setCurrentFilters] = useState<ExampleFilter[]>([]);

  const isValidFilter = useCallback((filter: ExampleFilter) => {
    return (
      (filter['type'] === 'full_text' && filter['value']) ||
      (filter['type'] === 'metadata' &&
        filter['value'] &&
        typeof filter['value'] === 'object' &&
        Object.keys(filter['value']).length === 1 &&
        Object.keys(filter['value'])[0] !== '' &&
        Object.values(filter['value'])[0] !== '' &&
        Object.values(filter['value'])[0] !== undefined)
    );
  }, []);

  const searchModel = useMemo(() => {
    return currentFilters.reduce((acc, filter) => {
      if (isValidFilter(filter)) {
        if (
          filter['type'] === 'full_text' &&
          typeof filter['value'] === 'string'
        ) {
          return {
            ...acc,
            full_text: [...(acc.full_text ?? []), filter['value'] as string],
          };
        }
        if (
          filter['type'] === 'metadata' &&
          typeof filter['value'] === 'object'
        ) {
          return {
            ...acc,
            metadata: {
              ...(acc.metadata ?? {}),
              ...filter['value'],
            },
          };
        }
      }
      return acc;
    }, {} as ExamplesSearchModel);
  }, [currentFilters, isValidFilter]);

  const [debouncedSearchModel] = useDebounce(searchModel, 500);
  const [newSplitModalOpen, setNewSplitModalOpen] = useState(false);
  const updateSplits = useUpdateDatasetSplitsMutation(datasetId, {
    onSuccess: () => {
      onMutate?.();
      datasetSplitsSwr.mutate();
      setNewSplitModalOpen(false);
    },
    onError: (e) => {
      createToast({
        title: 'Error updating splits',
        description: e.message,
        error: true,
      });
    },
  });

  const numActiveFilters =
    countNumQueries(debouncedSearchModel) + (filterToSplit ? 1 : 0);

  const filtersToUse = useMemo(() => {
    const userFilters = debouncedSearchModel.metadata
      ? {
          metadata: JSON.stringify(debouncedSearchModel.metadata),
        }
      : {};
    if (debouncedSearchModel.full_text) {
      userFilters['full_text_contains'] = debouncedSearchModel.full_text;
    }
    if (filterToSplit) {
      userFilters['splits'] = [filterToSplit];
    }
    return {
      ...filter,
      ...userFilters,
    };
  }, [
    debouncedSearchModel.metadata,
    debouncedSearchModel.full_text,
    filterToSplit,
    filter,
  ]);

  const selectFields = ['metadata', 'attachment_urls', 'outputs', 'inputs'];

  const {
    data: rows,
    isLoading,
    isValidating,
  } = useExamples({
    ...filtersToUse,
    offset: paginationModel.pageIndex * paginationModel.pageSize,
    limit: paginationModel.pageSize,
    order: sortingState[0].id as 'recently_created' | 'recent',
    descending: sortingState[0].desc,
    select: selectFields,
  });

  const { data: totalExamples } = useCountExamples(filtersToUse);

  const [deleteOpen, setDeleteOpen] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState<ExampleSchema | null>(null);
  const [deleteBulkModalOpen, setDeleteBulkModalOpen] = useState(false);
  const [uploadExamplesPaneOpen, setUploadExamplesPaneOpen] = useState(false);
  const [metadataColumns, setMetadataColumns] = useState<string[]>([]);
  const { authorize } = usePermissions();

  const shouldUseKVOnlyPane = dataType === 'kv';

  const handleChangeSplits = useCallback(
    async (split: string, remove: boolean) => {
      await updateSplits.trigger({
        json: {
          split_name: split,
          examples: Object.keys(rowSelection),
          remove,
        },
      });
      setIsCreatingSplit(false);
    },
    [rowSelection, updateSplits]
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const exampePeekId = searchParams.get('examplePeek');
  const setExamplePeekId = (runId: string | null) => {
    if (runId) {
      setSearchParams(
        (prev) => {
          prev.set('examplePeek', runId);
          return prev;
        },
        { replace: true }
      );
    } else {
      setSearchParams(
        (prev) => {
          prev.delete('examplePeek');
          return prev;
        },
        { replace: true }
      );
    }
  };

  const asOfRaw = searchParams.get('as_of');

  useEffect(() => {
    const currentMetadataColumns = rows?.reduce((acc, row) => {
      return [
        ...acc,
        ...(row.metadata
          ? Object.keys(row.metadata).filter((m) => m !== 'dataset_split')
          : []),
      ];
    }, [] as string[]);
    const currentMetadataColumnsSet = new Set(currentMetadataColumns);

    setMetadataColumns((prev) => {
      const metadataColumnsToAdd =
        Array.from(currentMetadataColumnsSet).filter(
          (column) => !prev.includes(column)
        ) ?? [];
      return [...prev, ...metadataColumnsToAdd];
    });
  }, [rows]);

  useEffect(() => {
    // Reset to the first page when the filters change
    resetPaginationModel();
  }, [
    numActiveFilters,
    filterToSplit,
    setPaginationModel,
    resetPaginationModel,
  ]);

  useEffect(() => {
    // Reset to the first page when the sorting changes
    resetPaginationModel();
  }, [sortingState, setPaginationModel, resetPaginationModel]);

  const columns = [
    getTableSelectColumnDef<ExampleSchema>({
      checkboxesHighlighted: isCreatingSplit,
      lastSelectedId,
      setLastSelectedId,
    }),
    ...COLUMNS_DEF,
    columnHelper.accessor('split' as any, {
      header: 'Splits',
      enableResizing: true,
      enableSorting: false,
      cell: ({ row }) => {
        let splits = row.original.metadata?.dataset_split ?? 'base';
        if (Array.isArray(splits)) {
          splits = splits.join(', ');
        }
        return (
          <div className="flex items-center gap-2 overflow-auto no-scrollbar">
            <div>{splits}</div>
          </div>
        );
      },
    }),
    ...metadataColumns.map((column) =>
      columnHelper.accessor(`metadata.${column}`, {
        header: () => {
          return (
            <div className="flex items-center gap-2">
              <MetadataIcon className="h-5 w-5" />
              <div>{column}</div>
            </div>
          );
        },
        cell: ({ row }) => {
          const metadataValue = row.original.metadata?.[column];
          const textToDisplay = (
            typeof metadataValue === 'string'
              ? metadataValue
              : metadataValue != null
              ? JSON.stringify(metadataValue)
              : '--'
          ) as string;
          return (
            <div className="overflow-auto py-3 no-scrollbar">
              {textToDisplay}
            </div>
          );
        },
        enableResizing: true,
        enableSorting: false,
        size: 150,
      })
    ),
    getActionColumn(isReadOnly),
  ];
  const table = useExamplesTable({
    rows,
    paginationModel,
    onPaginationChange: setPaginationModel,
    rowSelection,
    onRowSelectionChange: setRowSelection,
    columns,
    setDeleteOpen,
    setEditOpen,
    sortingState,
    onSortingChange: setSortingState,
    columnVisibilityModel,
    onColumnVisibilityChange: setColumnVisibilityModel,
    displayMode,
    language,
  });

  const [peekOpen, setPeekOpen] = useState(() => exampePeekId != null);

  const { createToast } = useToast();
  const [selectedDatasetForClone, setSelectedDatasetForClone] = useState<
    string | undefined
  >();

  const cloneDataset = useCloneDatasetMutation({
    onSuccess: () => {
      setIsCloneSelectedExamplesModalOpen(false);
      if (selectedDatasetForClone) {
        createToast({
          title: 'Examples Copied Successfully',
          description: (
            <Link
              to={`/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${selectedDatasetForClone}`}
              className="underline"
            >
              View the dataset
            </Link>
          ),
        });
      }
    },
    onError: (e) => {
      createToast({
        title: 'Error copying examples',
        description: e.message,
        error: true,
      });
    },
  });

  const examplePeekIdx = rows?.findIndex((i) => i.id === exampePeekId);

  const selectedExamples: ExampleSchema[] = useMemo(() => {
    if (!rows) {
      return [];
    }
    return Object.keys(rowSelection)
      .map((key) => rows.find((row) => row.id === key))
      .filter(Boolean) as ExampleSchema[];
  }, [rowSelection, rows]);

  const [isSyntheticExamplesPaneOpen, setIsSyntheticExamplesPaneOpen] =
    useState(false);

  const datasetSplitsSwr = useDatasetSplits(asOfRaw ?? 'latest', datasetId);
  const allSplits = useMemo(
    () => datasetSplitsSwr.data ?? [],
    [datasetSplitsSwr.data]
  );

  useEffect(() => {
    if (
      filterToSplit &&
      !allSplits.includes(filterToSplit) &&
      !datasetSplitsSwr.isLoading
    ) {
      setFilterToSplit(null);
    }
  }, [allSplits, datasetSplitsSwr.isLoading, filterToSplit]);

  const commonMetadata: Record<string, unknown[]> = useMemo(() => {
    return (
      rows?.reduce((acc, row) => {
        if (!row.metadata) {
          return acc;
        }
        Object.keys(row.metadata).forEach((key) => {
          if (
            typeof row.metadata?.[key] === 'object' &&
            row.metadata?.[key] !== null
          ) {
            return;
          }
          if (acc[key] == null) {
            acc[key] = [];
          }
          const currSet = new Set(acc[key]);
          currSet.add(row.metadata?.[key]);
          acc[key] = Array.from(currSet);
        });
        return acc;
      }, {}) ?? {}
    );
  }, [rows]);

  const dropdownOptions = useMemo(() => {
    const allSplitsOptions = allSplits.map((split) => ({
      splitValue: split,
      label: split === 'base' ? 'Base split' : split,
      icon: null,
      onSelect: null, // use default behavior
    }));
    return [
      {
        splitValue: null,
        label: 'View all examples',
        icon: null,
        onSelect: null,
      },
      ...allSplitsOptions,
      ...(isReadOnly
        ? []
        : [
            {
              splitValue: undefined,
              meta: null,
              label: 'New Split',
              icon: <PlusIcon className="h-4 w-4" />,
              onSelect: () => {
                if (isCreatingSplit && Object.keys(rowSelection).length > 0) {
                  setIsSplitPickerPopoverOpen(true);
                }
                setIsCreatingSplit(true);
              },
            },
          ]),
    ];
  }, [allSplits, isCreatingSplit, isReadOnly, rowSelection]);

  const [isExampleCrudPaneOpen, setIsExampleCrudPaneOpen] = useState(false);
  const [isNumberSelectorOpen, setIsNumberSelectorOpen] = useState(false);
  const [isExampleDropdownOpen, setIsExampleDropdownOpenState] =
    useState(false);
  const setIsExampleDropdownOpen = useCallback((open: boolean) => {
    setIsExampleDropdownOpenState(open);
    if (!open) {
      setIsNumberSelectorOpen(false);
    }
  }, []);
  const [numExamplesToGenerate, setNumExamplesToGenerate] = useState(5);
  const [selectionMethod, setSelectionMethod] = useState<
    'manual' | 'automatic'
  >('automatic');

  const doesntHaveFullSchema =
    !dataset?.inputs_schema_definition || !dataset?.outputs_schema_definition;
  const aiGeneratedExamplesDisabled = doesntHaveFullSchema || isReadOnly;

  const popoverDropdownMenuItems = [
    ...(authorize('datasets:update')
      ? [
          {
            onClick: () => setIsExampleCrudPaneOpen(true),
            icon: <PlusIcon className="h-5 w-5" />,
            title: 'Add Example',
          },
        ]
      : []),
    ...(authorize('datasets:update')
      ? [
          {
            onClick: () => setUploadExamplesPaneOpen(true),
            icon: <FilePlus02Icon className="h-5 w-5" />,
            title: 'Add Examples from File',
          },
        ]
      : []),
    ...(authorize('datasets:update') && exampleCount && exampleCount > 0
      ? [
          {
            onClick: null,
            icon: null,
            title: null,
            customCell: (
              <Popover
                open={isNumberSelectorOpen && !aiGeneratedExamplesDisabled}
                onOpenChange={(open) => {
                  setIsNumberSelectorOpen(open);
                }}
              >
                <PopoverTrigger
                  disabled={aiGeneratedExamplesDisabled}
                  className="w-full"
                >
                  <Tooltip
                    title={
                      doesntHaveFullSchema
                        ? 'Add a dataset schema to generate synthetic examples.'
                        : null
                    }
                  >
                    <span>
                      <CommandItem
                        className="mx-1.5 flex cursor-pointer flex-col items-center rounded-md px-[2px] py-0 aria-selected:bg-secondary"
                        disabled={aiGeneratedExamplesDisabled}
                      >
                        <PopoverDropdownMenuItem
                          icon={<StarsIcon className="mr-[2px] h-4 w-4" />}
                          title="Add AI-Generated Examples"
                          selected={false}
                          titleBold
                          disabled={aiGeneratedExamplesDisabled}
                        />
                      </CommandItem>
                    </span>
                  </Tooltip>
                </PopoverTrigger>
                <PopoverContent
                  side="left"
                  align="start"
                  sideOffset={5}
                  className="w-[400px] rounded-lg border border-secondary bg-popover shadow-md"
                  onInteractOutside={(e) => {
                    if (selectionMethod === 'manual') {
                      e.preventDefault();
                    }
                  }}
                >
                  <GenerateSyntheticExamplesPopoverContent
                    selectedExampleIds={Object.keys(selectedExamples)}
                    setIsNumberSelectorOpen={(open) => {
                      setIsNumberSelectorOpen(open);
                      setIsExampleDropdownOpen(false);
                    }}
                    numExamplesToGenerate={numExamplesToGenerate}
                    setNumExamplesToGenerate={setNumExamplesToGenerate}
                    setIsSyntheticExamplesPaneOpen={
                      setIsSyntheticExamplesPaneOpen
                    }
                    selectionMethod={selectionMethod}
                    setSelectionMethod={setSelectionMethod}
                  />
                </PopoverContent>
              </Popover>
            ),
          },
        ]
      : []),
  ];

  if (!rows) {
    return <LinearProgress />;
  }

  return (
    <>
      <div className="mb-4 flex justify-between">
        <div className="flex gap-3">
          <ExamplesFilterBar
            filters={currentFilters}
            onChange={setCurrentFilters}
            commonMetadata={commonMetadata}
            value={searchModel}
          />

          <PopoverDropdownMenu
            values={dropdownOptions}
            getTitle={(value) => value.label}
            getIsSelected={(value) => value.splitValue === filterToSplit}
            getIsTitleBold={(value) => value.splitValue === filterToSplit}
            onSelect={(value) => {
              if (value.onSelect) {
                value.onSelect();
              } else {
                setFilterToSplit(value.splitValue);
              }
            }}
            getIcon={(value) => value.icon}
          >
            <Button
              variant="normal"
              color="secondary"
              leftDecorator={ChevronDownIcon}
              size="md"
            >
              {filterToSplit == null
                ? 'Select split'
                : filterToSplit === 'base'
                ? 'Base split'
                : `Split: ${filterToSplit}`}
            </Button>
          </PopoverDropdownMenu>

          {/* Display Mode Toggle */}
          <div className="flex items-center gap-2">
            {showExamplesTableToggles && (
              <GroupedTabs
                value={displayMode}
                onChange={(value) =>
                  setDisplayMode(value as 'compact' | 'full')
                }
                options={[
                  { display: 'Compact', value: 'compact' },
                  { display: 'Full', value: 'full' },
                ]}
                size="md"
              />
            )}

            {/* JSON/YAML Toggle - only enabled in Full mode */}
            {showExamplesTableToggles && (
              <GroupedTabs
                value={language}
                onChange={(value) => setLanguage(value as CodeLanguageType)}
                options={[
                  {
                    display: 'JSON',
                    value: 'json',
                    disabled: displayMode === 'compact',
                    tooltip:
                      displayMode === 'compact'
                        ? 'Switch to Full mode to change format'
                        : undefined,
                  },
                  {
                    display: 'YAML',
                    value: 'yaml',
                    disabled: displayMode === 'compact',
                    tooltip:
                      displayMode === 'compact'
                        ? 'Switch to Full mode to change format'
                        : undefined,
                  },
                ]}
                size="md"
              />
            )}
          </div>
          {showExamplesTableToggles && (
            <DataGridColumnVisibilityPopover
              table={table}
              localStorageKey={COLUMN_VISIBILITY_STATE_KEY}
            />
          )}
        </div>
        {popoverDropdownMenuItems.length > 0 && (
          <PopoverDropdownMenu
            isOpen={isExampleDropdownOpen}
            setIsOpen={(open) => {
              if (
                !open &&
                selectionMethod === 'manual' &&
                isNumberSelectorOpen
              ) {
                return;
              }
              if (popoverDropdownMenuItems.length > 1) {
                setIsExampleDropdownOpen(open);
              } else {
                setIsExampleCrudPaneOpen(open);
              }
            }}
            values={popoverDropdownMenuItems}
            getIcon={(value) => value.icon}
            getTitle={(value) => value.title ?? ''}
            getIsSelected={() => false}
            onSelect={(value) => value.onClick?.()}
            getCustomCell={(value) => value.customCell}
            asChild
          >
            <Button
              size="md"
              variant="normal"
              leftDecorator={PlusIcon}
              disabled={isReadOnly}
            >
              Example
            </Button>
          </PopoverDropdownMenu>
        )}
      </div>
      <div className="overflow-hidden rounded-lg border border-secondary">
        <DataGrid
          table={table}
          cellHeight={56}
          cellSpacingVariant="wide"
          isLoading={isLoading || isValidating}
          withCustomTextOverflow={displayMode === 'full'}
          emptyState={
            numActiveFilters ? (
              <div className="text-tertiary">
                There are no examples for your current filters
              </div>
            ) : (
              <div className="text-tertiary">There are no examples yet</div>
            )
          }
          onClick={(row, event) => {
            if (
              !emulateNativeClick(
                `/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${datasetId}/${appExamplePath}/${row.id}`,
                event.nativeEvent
              )
            ) {
              setExamplePeekId(row.id);
              setPeekOpen(true);
            }
          }}
          onMiddleClick={(row) => {
            if (
              !emulateNativeMiddleClick(
                `/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${datasetId}/${appExamplePath}/${row.id}`
              )
            ) {
              navigate(`${appExamplePath}/${row.id}`);
            }
          }}
          highlightedId={exampePeekId}
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-tertiary">
          {totalExamples != null &&
            (totalExamples === 0
              ? ''
              : {
                  one: `1 example in total`,
                  two: `${totalExamples} examples in total`,
                  few: `${totalExamples} examples in total`,
                  other: `${totalExamples} examples in total`,
                }[pluralRules.select(totalExamples)])}
        </div>
        <DataGridPagination table={table} />
      </div>

      {editOpen &&
        (shouldUseKVOnlyPane ? (
          <KVOnlyExampleCrudPane
            dataset={dataset}
            example={editOpen}
            isOpen={!!editOpen}
            doClose={() => setEditOpen(null)}
            onSuccess={() => {
              onMutate?.();
              setEditOpen(null);
            }}
          />
        ) : (
          <ExampleCrudPane
            dataset={dataset}
            example={editOpen}
            isOpen={!!editOpen}
            doClose={() => setEditOpen(null)}
            onSuccess={() => {
              onMutate?.();
              setEditOpen(null);
            }}
          />
        ))}

      <DeleteModal
        endpoint={apiExamplesPath}
        id={deleteOpen as string}
        isOpen={!!deleteOpen}
        doClose={() => {
          onMutate?.();
          setDeleteOpen(null);
        }}
        invalidationPrefixes={[apiDatasetsPath]}
        modalText="Are you sure you want to delete this example from your dataset?"
      />
      {dataset ? (
        <SyntheticExamplesPane
          dataset={dataset}
          isOpen={isSyntheticExamplesPaneOpen}
          doClose={() => {
            setIsSyntheticExamplesPaneOpen(false);
          }}
          onMutate={onMutate}
          selectedExampleIds={selectedExamples.map((e) => e.id)}
          numExamplesToGenerate={numExamplesToGenerate}
          selectionMethod={selectionMethod}
        />
      ) : null}

      {shouldUseKVOnlyPane ? (
        <KVOnlyExampleCrudPane
          dataset={dataset}
          isOpen={isExampleCrudPaneOpen}
          doClose={() => setIsExampleCrudPaneOpen(false)}
          onSuccess={onMutate}
        />
      ) : (
        <ExampleCrudPane
          dataset={dataset}
          isOpen={isExampleCrudPaneOpen}
          doClose={() => setIsExampleCrudPaneOpen(false)}
          onSuccess={onMutate}
        />
      )}

      {(Object.keys(rowSelection).length > 0 || isCreatingSplit) && (
        <div className="fixed bottom-3 left-1/2 z-[100] flex -translate-x-1/2 items-center gap-6 whitespace-nowrap rounded-md border border-secondary bg-popover px-6 py-4 shadow-md">
          <Text variant="h2" className="shrink-0">
            {Object.keys(rowSelection).length === 0 ? (
              <>Select Examples</>
            ) : Object.keys(rowSelection).length === 1 ? (
              <>Selected 1 Example</>
            ) : (
              <>Selected {Object.keys(rowSelection).length} Examples</>
            )}
          </Text>

          <div className="flex items-center gap-2">
            <Popover
              open={isSplitPickerPopoverOpen && !isCreatingSplit}
              onOpenChange={(open) => {
                if (!isCreatingSplit) {
                  setIsSplitPickerPopoverOpen(open);
                }
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  size="md"
                  color="primary"
                  disabled={
                    Object.keys(rowSelection).length === 0 || isReadOnly
                  }
                  onClick={() => {
                    if (isCreatingSplit) {
                      setNewSplitModalOpen(true);
                    }
                  }}
                >
                  {isCreatingSplit ? 'Create New Split' : 'Add to Split'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-96 p-0">
                <SplitPickerPopoverContent
                  selectedExamples={selectedExamples}
                  splits={allSplits}
                  onSelectSplit={handleChangeSplits}
                  isMutating={updateSplits.isMutating}
                  newSplitModalOpen={newSplitModalOpen}
                  setNewSplitModalOpen={setNewSplitModalOpen}
                />
              </PopoverContent>
            </Popover>
            {Object.keys(rowSelection).length > 0 && (
              <>
                <Button
                  size="md"
                  variant="outlined"
                  onClick={() => setIsCloneSelectedExamplesModalOpen(true)}
                >
                  Copy to Another Dataset
                </Button>
                <Button
                  size="md"
                  color="error"
                  variant="outlined"
                  onClick={() => setDeleteBulkModalOpen(true)}
                  disabled={isReadOnly}
                >
                  Delete
                </Button>
              </>
            )}

            <IconButton
              size="sm"
              icon={XMarkIcon}
              variant="plain"
              color="secondary"
              onClick={() => {
                setRowSelection({});
                setIsCreatingSplit(false);
              }}
              label="Clear selection"
            />
          </div>

          <Modal
            open={newSplitModalOpen}
            onClose={() => setNewSplitModalOpen(false)}
            title="Create New Split"
          >
            <ModalDialog>
              <form
                className="flex flex-col gap-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleChangeSplits(newSplitName, false);
                }}
              >
                <Text variant="h2">Split name</Text>
                <Input
                  type="text"
                  placeholder="Name..."
                  value={newSplitName}
                  onChange={(e) => setNewSplitName(e)}
                />
                <div className="flex justify-end gap-2">
                  <Button
                    size="md"
                    onClick={() => setNewSplitModalOpen(false)}
                    variant="outlined"
                    color="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={updateSplits.isMutating}
                    size="md"
                    leftDecorator={PlusIcon}
                  >
                    Create
                  </Button>
                </div>
              </form>
            </ModalDialog>
          </Modal>

          <MultiDeleteModal
            isOpen={!!deleteBulkModalOpen}
            doClose={(didSucceed) => {
              if (didSucceed) {
                setRowSelection({});
              }
              setDeleteBulkModalOpen(false);
            }}
            endpoint={apiExamplesPath}
            idParamName="example_ids"
            items={selectedExamples}
            invalidationPrefixes={[apiDatasetsPath]}
            modalText={`Are you sure you want to delete ${
              Object.keys(rowSelection).length > 1
                ? `${Object.keys(rowSelection).length} examples`
                : 'this example'
            } from your dataset?`}
          />
        </div>
      )}
      <SplitViewPane
        open={peekOpen}
        sidePaneId="examplesTable"
        onClose={() => {
          setPeekOpen(false);
          setExamplePeekId(null);
        }}
        onExpand={() => {
          if (exampePeekId) {
            navigate(
              `${appExamplePath}/${exampePeekId}${
                asOfRaw ? `?as_of=${encodeURIComponent(asOfRaw)}` : ''
              }`
            );
          }
        }}
        title=""
        onNext={
          examplePeekIdx != null && examplePeekIdx < rows.length - 1
            ? () => setExamplePeekId(rows[examplePeekIdx + 1].id)
            : undefined
        }
        onPrevious={
          examplePeekIdx != null && examplePeekIdx > 0
            ? () => setExamplePeekId(rows[examplePeekIdx - 1].id)
            : undefined
        }
        className="relative"
      >
        <div className="absolute inset-0 flex flex-grow flex-col gap-4 overflow-auto px-4">
          <Example exampleId={exampePeekId} onMutate={onMutate} />
        </div>
      </SplitViewPane>
      <CloneDatasetExamplesModal
        open={isCloneSelectedExamplesModalOpen}
        onClose={() => setIsCloneSelectedExamplesModalOpen(false)}
        sourceDatasetId={datasetId ?? null}
        sourceDataType={dataType}
        examples={Object.keys(rowSelection)}
        isLoading={cloneDataset.isMutating}
        setTargetDatasetId={setSelectedDatasetForClone}
        targetDatasetId={selectedDatasetForClone}
        prefilledData={{
          inputs_schema_definition: dataset?.inputs_schema_definition,
          outputs_schema_definition: dataset?.outputs_schema_definition,
        }}
        onSubmit={() => {
          if (datasetId && selectedDatasetForClone) {
            cloneDataset.trigger({
              json: {
                source_dataset_id: datasetId,
                target_dataset_id: selectedDatasetForClone,
                examples: Object.keys(rowSelection),
                as_of: asOfRaw ?? undefined,
              },
            });
          }
        }}
      />
      {dataset && (
        <UploadExamplesToExistingDatasetPane
          open={uploadExamplesPaneOpen}
          onClose={() => setUploadExamplesPaneOpen(false)}
          dataset={dataset}
          onSuccess={() => {
            onMutate?.();
          }}
        />
      )}
    </>
  );
};
